import React, { useEffect, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Center,
  Group,
  LoadingOverlay,
  Popover,
  Stack,
  Text,
  Title,
  createStyles,
} from '@mantine/core';
import { DatePicker, DateValue } from '@mantine/dates';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import {
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconExclamationCircle,
} from '@tabler/icons-react';
import { IEventTypeMultiSelectAutoComplete } from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useClassAthleteAgenda from 'hooks/class/useClassAthleteAgenda';
import { useLocation } from 'react-router-dom';
import { KiloClassAthleteAgenda } from 'types';

import ClassCard from './components/ClassCard';

const useStyles = createStyles(theme => ({
  dateSelectContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    justifyItems: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
  },
}));

const today = new Date();
today.setHours(0);
today.setMinutes(0);
today.setSeconds(0);

interface FilterTerms {
  eventTypes: IEventTypeMultiSelectAutoComplete[];
  coaches: string[];
  locations: string[];
}

interface ClassEvent {
  id: string;
  type: 'class';
  sortField: string;
  kiloClass: KiloClassAthleteAgenda;
}

interface ClassesViewProps {
  searchFilters: FilterTerms;
}

const ClassesView = ({ searchFilters }: ClassesViewProps): JSX.Element => {
  const { classes } = useStyles();
  const { session } = useAuth();
  const { state } = useLocation();
  const initialSelectedDate =
    state?.calendarDate && dayjs(state.calendarDate).isValid()
      ? dayjs(state.calendarDate).startOf('day').toDate()
      : today;

  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const [isShowCalendar, { open: showCalendar, close: hideCalendar, toggle: toggleCalendarVisibility }] =
    useDisclosure(false);
  const [debouncedSearchFilters] = useDebouncedValue<FilterTerms>(searchFilters, 250);
  const [debouncedSelectedDate] = useDebouncedValue(selectedDate, 250);
  const [classEvents, setClassEvents] = useState<ClassEvent[]>([]);

  const handlePrevious = (): void => {
    setIsEventsLoading(true);
    setSelectedDate(currentSelectedDate => dayjs(currentSelectedDate).subtract(1, 'day').toDate());
  };

  const handleNext = (): void => {
    setIsEventsLoading(true);
    setSelectedDate(currentSelectedDate => dayjs(currentSelectedDate).add(1, 'day').toDate());
  };

  const handleCalendarChange = (newSelectedDate: DateValue): void => {
    if (newSelectedDate) {
      setSelectedDate(newSelectedDate);
      hideCalendar();
    }
  };

  const {
    data: classData,
    isError: classIsError,
    isLoading: classIsLoading,
    isSuccess: classIsSuccess,
  } = useClassAthleteAgenda({
    ...searchFilters,
    eventTypes: debouncedSearchFilters.eventTypes?.map(i => i.eventTypeId),
    locations: debouncedSearchFilters.locations.map(location => location.replace('noLocation', 'None')),
    coaches: debouncedSearchFilters.coaches.map(coach => coach.replace('noStaff', 'None')),
    date: dayjs(debouncedSelectedDate).format('YYYY-MM-DD'),
  });

  useEffect(() => {
    if (classIsSuccess && classData && session?.athlete?.athleteId) {
      const kiloClasses: ClassEvent[] = classData.data.map((event: KiloClassAthleteAgenda) => {
        const fullStartDate = new Date(`${event.startDate}T${event.startTime}`);

        return {
          id: event.kiloClassId,
          type: 'class',
          sortField: fullStartDate.toISOString(),
          kiloClass: event,
        };
      });

      const sortedEvents = kiloClasses.sort((previous, current) => {
        if (previous.sortField < current.sortField) {
          return -1;
        }
        if (previous.sortField > current.sortField) {
          return 1;
        }
        return 0;
      });

      setClassEvents(sortedEvents);
      setIsEventsLoading(false);
    }
  }, [classIsSuccess, classData, session?.athlete?.athleteId, setIsEventsLoading]);

  return (
    <>
      <LoadingOverlay radius='lg' visible={isEventsLoading} />

      <div className={classes.dateSelectContainer}>
        <ActionIcon ml='auto' radius='lg' size='xl' color='kiloBlue' onClick={handlePrevious}>
          <IconChevronLeft size='1.625rem' />
        </ActionIcon>
        <Popover
          opened={isShowCalendar}
          onOpen={showCalendar}
          onClose={hideCalendar}
          closeOnEscape
          offset={26}
          radius='lg'
          width='xl'
          position='bottom'
          shadow='md'
          keepMounted
        >
          <Popover.Target>
            <Group onClick={toggleCalendarVisibility} noWrap spacing='sm'>
              <Title order={4} color='dark.3' align='center'>
                {dayjs(selectedDate).format('dddd, MMMM D')}
              </Title>
              <IconCalendarEvent stroke={1.8} size='1.5rem' />
            </Group>
          </Popover.Target>
          <Popover.Dropdown>
            <DatePicker
              size='lg'
              minDate={today}
              value={selectedDate}
              defaultDate={selectedDate}
              onChange={handleCalendarChange}
            />
          </Popover.Dropdown>
        </Popover>

        <ActionIcon mr='auto' radius='lg' size='xl' color='kiloBlue' onClick={handleNext}>
          <IconChevronRight size='1.625rem' />
        </ActionIcon>
      </div>

      {!classIsLoading && classEvents.length === 0 && (
        <Center>
          <Text color='dimmed'>No classes found. Try changing filters.</Text>
        </Center>
      )}

      {classIsError && (
        <Center sx={{ flexGrow: 1 }} p='lg'>
          <Alert icon={<IconExclamationCircle size='1rem' />} title='Error' color='red'>
            Something went wrong while fetching class data!
          </Alert>
        </Center>
      )}

      {classEvents.length > 0 && (
        <Stack spacing='md'>
          {classEvents.map(event => (
            <ClassCard
              key={event.id}
              kiloClass={event.kiloClass}
              selectedDate={selectedDate}
              searchFilters={searchFilters}
            />
          ))}
        </Stack>
      )}
    </>
  );
};

export default ClassesView;
